import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { contactService, contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Contact = () => {
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { getText, isEnglish } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });

  // Default values - Chinese
  const defaultOfficeHours = [
    '週一 - 週五|9:00 AM - 6:00 PM',
    '週六|9:00 AM - 5:00 PM',
    '週日 & 公眾假期|休息'
  ];

  // Default values - English
  const defaultOfficeHoursEn = [
    'Monday - Friday|9:00 AM - 6:00 PM',
    'Saturday|9:00 AM - 5:00 PM',
    'Sunday & Public Holidays|Closed'
  ];

  const defaultWhyChooseUs = [
    '專業團隊，豐富經驗',
    '24小時客戶支援',
    '兩年合約保證',
    '免費更換服務',
    '透明收費，無隱藏費用'
  ];

  const defaultWhyChooseUsEn = [
    'Professional team with rich experience',
    '24-hour customer support',
    'Two-year contract guarantee',
    'Free replacement service',
    'Transparent pricing, no hidden fees'
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get office hours with correct language
  const getOfficeHours = () => {
    if (!content?.officeHours || content.officeHours.length === 0) {
      return isEnglish ? defaultOfficeHoursEn : defaultOfficeHours;
    }
    
    if (isEnglish) {
      if (content.officeHoursEn && content.officeHoursEn.length > 0) {
        return content.officeHoursEn.map((en, idx) => en || defaultOfficeHoursEn[idx] || content.officeHours[idx]);
      }
      return defaultOfficeHoursEn;
    }
    return content.officeHours;
  };
  
  // Get why choose us with correct language
  const getWhyChooseUs = () => {
    if (!content?.whyChooseUs || content.whyChooseUs.length === 0) {
      return isEnglish ? defaultWhyChooseUsEn : defaultWhyChooseUs;
    }
    
    if (isEnglish) {
      if (content.whyChooseUsEn && content.whyChooseUsEn.length > 0) {
        return content.whyChooseUsEn.map((en, idx) => en || defaultWhyChooseUsEn[idx] || content.whyChooseUs[idx]);
      }
      return defaultWhyChooseUsEn;
    }
    return content.whyChooseUs;
  };

  const officeHours = getOfficeHours();
  const whyChooseUs = getWhyChooseUs();

  const contactInfo = [
    {
      icon: <Phone className="h-8 w-8" style={{ color: '#00b6a5' }} />,
      title: getText('24小時客戶支援熱線', '24-Hour Customer Support'),
      content: content?.phone || '+852 6187 1388',
      link: `tel:${(content?.phone || '+852 6187 1388').replace(/\s/g, '')}`
    },
    {
      icon: <Mail className="h-8 w-8" style={{ color: '#00b6a5' }} />,
      title: getText('電子郵箱', 'Email'),
      content: content?.email || 'info@maidful.com.hk',
      link: `mailto:${content?.email || 'info@maidful.com.hk'}`
    },
    {
      icon: <MapPin className="h-8 w-8" style={{ color: '#00b6a5' }} />,
      title: getText('辦公地址', 'Office Address'),
      content: isEnglish ? (content?.addressEn || 'Kowloon, Hong Kong') : (content?.address || '香港九龍'),
      link: null
    },
    {
      icon: <Clock className="h-8 w-8" style={{ color: '#00b6a5' }} />,
      title: getText('辦公時間', 'Office Hours'),
      content: officeHours[0]?.split('|')[1] || '9:00 AM - 6:00 PM',
      link: null
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await contactService.submit(formData);
      
      toast({
        title: getText('謝謝您的查詢！', 'Thank you for your inquiry!'),
        description: getText('我們已收到您的訊息，會盡快與您聯絡。', 'We have received your message and will contact you soon.'),
      });

      setFormData({
        name: '',
        phone: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: getText('提交失敗', 'Submission Failed'),
        description: getText('請稍後再試或直接致電聯絡我們。', 'Please try again later or call us directly.'),
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              <span style={{ color: '#00b6a5' }}>{getText('聯絡我們', 'Contact Us')}</span>
            </h1>
            <p className="text-xl text-gray-600">{getText('我們隨時準備好為您服務', 'We are always ready to serve you')}</p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-8 pb-6">
                  <div className="flex justify-center mb-4">{info.icon}</div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: '#00b6a5' }}>
                    {info.title}
                  </h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-gray-700">{info.content}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Quick Contact */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ color: '#00b6a5' }}>
                    {getText('留下訊息', 'Leave a Message')}
                  </CardTitle>
                  <p className="text-gray-600">{getText('填寫以下表格，我們會盡快與您聯絡', 'Fill out the form below and we will contact you soon')}</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('姓名', 'Name')} *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={getText('請輸入您的姓名', 'Enter your name')}
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        聯絡電話 *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+852 XXXX XXXX"
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        電子郵箱
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@email.com"
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        主題 *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="例如：請詢海外女傭費用"
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        訊息 *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="請詳細描述您的需求..."
                        disabled={submitting}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      style={{ backgroundColor: '#00b6a5' }}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          正在提交...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          發送訊息
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Quick Contact */}
            <div className="space-y-6">
              <Card className="text-center p-8" style={{ background: 'linear-gradient(135deg, #00b6a5 0%, #00d4bd 100%)' }}>
                <CardContent>
                  <h3 className="text-3xl font-bold text-white mb-4">
                    需要立即協助？
                  </h3>
                  <p className="text-xl text-white mb-6">
                    透過WhatsApp聯絡我們，即時回覆！
                  </p>
                  <a
                    href={`https://api.whatsapp.com/send?phone=${(content?.whatsapp || '85261871388').replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="lg"
                      className="w-full text-lg py-6"
                      style={{ backgroundColor: '#c8ff00', color: '#000' }}
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      WhatsApp 聯絡
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Why Choose Us - Dynamic */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: '#00b6a5' }}>為什麼選擇我們？</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    {whyChooseUs.map((reason, index) => (
                      <li key={index} className="flex items-start">
                        <span className="inline-block w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: '#00b6a5' }}></span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Office Hours - Dynamic */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: '#00b6a5' }}>辦公時間</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-gray-700">
                    {officeHours.map((hour, index) => {
                      const parts = hour.split('|');
                      return (
                        <div key={index} className="flex justify-between">
                          <span className="font-medium">{parts[0]}</span>
                          <span>{parts[1]}</span>
                        </div>
                      );
                    })}
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-600">
                        * 24小時緊急熱線隨時為您服務
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>
              我們的位置
            </h2>
          </div>
          <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-16 w-16 mx-auto mb-4" style={{ color: '#00b6a5' }} />
              <p className="text-xl font-semibold text-gray-700">{content?.address || '香港九龍'}</p>
              <p className="text-gray-600 mt-2">歡迎預約參觀我們的辦公室</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
